/**
 * RiskRadarChips
 *
 * Small stacked risk-signal chips shown only when flagged.
 * Max 3 visible + "+N more" rollup.
 * Each chip wraps in a GlossaryTooltip for rich structured explanations.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { GlossaryTooltip } from './GlossaryTooltip';
import { Tooltip } from '@/components/ui/Tooltip';
import type { PortfolioClient } from '@/data/portfolio.types';
import { formatRunsDryOn, isLowPipeline } from '@/utils/lowPipeline';
import { usePortfolioLocks } from '@/hooks/usePortfolioLocks';

interface RiskChip {
  key: string;
  label: string;
  level: 'red' | 'yellow';
  termId: string;
  /** Plain tooltip (used instead of the glossary card when the text carries a date) */
  tooltip?: string;
}

function buildChips(
  client: PortfolioClient,
  t: TFunction<'agency'>,
  locale: string,
  isLocked: (featureKey: string) => boolean,
): RiskChip[] {
  const chips: RiskChip[] = [];
  // Each chip family follows the package feature of the field it reads
  const relationshipLocked = isLocked('portfolio_client_relationship');
  const channelHealthLocked = isLocked('portfolio_channel_health');
  const runwayLocked = isLocked('portfolio_runway');
  const velocityLocked = isLocked('portfolio_velocity');

  // Red: Deprioritization risk
  if (!relationshipLocked && client.renewal.atRisk) {
    chips.push({
      key: 'deprioritization',
      label: t('portfolio_risk_chip_deprioritization'),
      level: 'red',
      termId: 'signal-deprioritization-risk',
    });
  }

  // Red: Broken channels
  if (!channelHealthLocked && client.channelHealth.broken > 0) {
    chips.push({
      key: 'broken_channels',
      label: t('portfolio_risk_chip_channels_broken', { count: client.channelHealth.broken }),
      level: 'red',
      termId: 'signal-channels-broken',
    });
  }

  // Yellow: Posts stuck in approval queue.
  // - For Carte Blanche teams there is no approval queue, so this signal is N/A
  //   (their UNSCHEDULED drafts are content-in-progress, not items "stale" in review).
  // - For approval-gated teams, fire on the actual PENDING (awaiting review) count.
  const awaitingApproval = client.pipeline?.awaitingApproval ?? 0;
  const isStale =
    !client.carteBlanche &&
    awaitingApproval >= 3 &&
    (client.healthOverall === 'watch' ||
      client.healthOverall === 'at_risk' ||
      client.healthOverall === 'off_track');
  if (isStale) {
    chips.push({
      key: 'stale',
      label: t('portfolio_risk_chip_awaiting_review', { n: awaitingApproval }),
      level: 'yellow',
      termId: 'signal-stale',
    });
  }

  // Yellow: Review cliff
  if (
    !relationshipLocked &&
    client.renewal.daysUntilRenewal !== null &&
    client.renewal.daysUntilRenewal < 30
  ) {
    chips.push({
      key: 'review_cliff',
      label: t('portfolio_risk_chip_review_in', { days: client.renewal.daysUntilRenewal }),
      level: 'yellow',
      termId: 'signal-review-cliff',
    });
  }

  // Yellow: Expiring channels
  if (!channelHealthLocked && client.channelHealth.expiring > 0) {
    chips.push({
      key: 'expiring_channels',
      label: t('portfolio_risk_chip_channels_expiring', { count: client.channelHealth.expiring }),
      level: 'yellow',
      termId: 'signal-channels-expiring',
    });
  }

  // Yellow: Scheduling cliff
  if (!runwayLocked && client.runwayDays <= 0 && client.healthOverall !== 'paused') {
    chips.push({
      key: 'scheduling_cliff',
      label: t('nothing_scheduled_label', { defaultValue: 'Nothing scheduled' }),
      level: 'yellow',
      termId: 'signal-scheduling-cliff',
    });
  }

  // Yellow: Velocity drop
  if (!velocityLocked && client.velocity.trendPct < -25) {
    chips.push({
      key: 'velocity',
      label: t('portfolio_risk_chip_velocity', { pct: client.velocity.trendPct }),
      level: 'yellow',
      termId: 'signal-velocity-drop',
    });
  }

  // Low pipeline: less than 2 weeks of planned + ready content (server SSOT, no client math)
  if (!runwayLocked && isLowPipeline(client)) {
    const date = formatRunsDryOn(client.runsDryOn, locale);
    chips.push({
      key: 'low_pipeline',
      label: t('low_pipeline_label', { defaultValue: 'Low pipeline · runs dry around {{date}}', date }),
      level: client.coverDays <= 0 ? 'red' : 'yellow',
      termId: 'signal-low-pipeline',
      tooltip: t('low_pipeline_tooltip', {
        defaultValue: 'Less than 2 weeks of planned and ready content. Runs dry around {{date}}.',
        date,
      }),
    });
  }

  return chips;
}

interface ChipButtonProps {
  chip: RiskChip;
}

const ChipButton = ({ chip }: ChipButtonProps) => {
  const colorClasses =
    chip.level === 'red'
      ? 'bg-coral-50 text-coral-700 border border-coral-200'
      : 'bg-peach/30 text-coral-700 border border-peach';

  const dotColor = chip.level === 'red' ? 'bg-coral-500' : 'bg-peach';

  const button = (
    <button
      type="button"
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap transition-[filter,border-color] hover:brightness-95 outline-none ${colorClasses} focus-visible:border-gray-900`}
      aria-label={chip.tooltip ? `${chip.label}. ${chip.tooltip}` : chip.label}
      onClick={(e) => e.stopPropagation()}
    >
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dotColor}`} aria-hidden="true" />
      {chip.label}
    </button>
  );

  if (chip.tooltip) {
    return (
      <Tooltip content={chip.tooltip} side="top">
        {button}
      </Tooltip>
    );
  }

  return (
    <GlossaryTooltip termId={chip.termId} side="top" align="start" frameShape="pill">
      {button}
    </GlossaryTooltip>
  );
};

interface RiskRadarChipsProps {
  client: PortfolioClient;
}

const MAX_VISIBLE = 3;

export const RiskRadarChips = ({ client }: RiskRadarChipsProps) => {
  const { t, i18n } = useTranslation('agency');
  const [showAll, setShowAll] = useState(false);
  const { isLocked } = usePortfolioLocks();
  const chips = buildChips(client, t, i18n.language, isLocked);

  if (chips.length === 0) return null;

  const visible = showAll ? chips : chips.slice(0, MAX_VISIBLE);
  const overflow = chips.length - MAX_VISIBLE;

  return (
    <div
      className="flex flex-wrap items-center gap-1"
      onClick={(e) => e.stopPropagation()}
    >
      {visible.map((chip) => (
        <ChipButton key={chip.key} chip={chip} />
      ))}

      {!showAll && overflow > 0 && (
        <button
          type="button"
          className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 transition-colors border border-transparent outline-none focus-visible:border-primary-600"
          onClick={(e) => {
            e.stopPropagation();
            setShowAll(true);
          }}
          aria-label={t('portfolio_risk_chip_show_more_aria', { count: overflow })}
        >
          {t('portfolio_risk_chip_more', { n: overflow })}
        </button>
      )}

      {showAll && chips.length > MAX_VISIBLE && (
        <button
          type="button"
          className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium text-gray-400 hover:text-gray-600 border border-transparent focus:outline-none focus-visible:border-primary-600"
          onClick={(e) => {
            e.stopPropagation();
            setShowAll(false);
          }}
          aria-label={t('portfolio_risk_chip_show_fewer_aria')}
        >
          {t('portfolio_risk_chip_less')}
        </button>
      )}
    </div>
  );
};
